import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {createPopper, Instance} from "@popperjs/core";
import {debounce} from "lodash";
import {CommonModule} from "@angular/common";
import {HighlightComponent} from "./highlight/highlight.component";
import {lodash} from "../utils";

@Component({
    selector: 'app-typeahead-window',
    templateUrl: './typeahead-window.component.html',
    styleUrls: ['./typeahead-window.component.scss'],
    imports: [
        CommonModule, HighlightComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})
export class TypeaheadWindowComponent {
  values: any[] = [];
  popperInstance: Instance;
  activeIndex = 0;

  @ViewChild("dropdownMenu") dropdownMenu: ElementRef;
  @Input() term: string;
  @Output() activeChanged: EventEmitter<HTMLElement> = new EventEmitter<HTMLElement>();
  showDelete: boolean = false;
  static incrementalId: number = 0;

  data: TypeaheadData;
  private debounceWait = 10;
  el: ElementRef;

  constructor(private changeDetector: ChangeDetectorRef, element: ElementRef) {
    this.el = element;
    $(element.nativeElement).attr("id", `typeahead-window-${TypeaheadWindowComponent.incrementalId}`);
    TypeaheadWindowComponent.incrementalId++;
  }

  openTypeaheadWindow = (event: TypeaheadData) => {
    this.data = event;
    this.showDelete = event.showDelete;
    if (event.results.length === 0) {
      this.closeWindow();
      return;
    }
    this.values = event.results;
    this.popperInstance = createPopper(event.input, this.dropdownMenu.nativeElement, {
      placement: "bottom-start"
    });
    setTimeout(() => {
      $(this.dropdownMenu.nativeElement).addClass('show');
      this.popperInstance.update();
      if (event.selectedValue) {
        this.focusDropdownItem(Math.max(0, lodash.findIndex(this.values, item => lodash.isEqual(item, event.selectedValue))));
      } else {
        this.focusFirstItem();
      }
      this.addListeners();
      this.changeDetector.markForCheck();
      $(this.data.input).attr("aria-expanded", 'true');
    }, 10);
  }

  onSelect = (index: number) => {
    if (!this.dropdownVisible()) {
      return;
    }
    this.data.selectItem.emit({
      item: this.data.results[index] || null,
      value: this.values[index] || null
    });
    this.closeWindow();
  }

  onDelete = (index: number) => {
    this.data.deleteItem.emit({
      item: this.data.results[index] || null,
      value: this.values[index] || null
    });
    this.onSelect(null);
  }

  dropdownVisible = () => $(this.dropdownMenu.nativeElement).hasClass("show");

  closeWindow = () => {
    if (this.data) {
      $(this.data.input).attr("aria-expanded", 'true');
      $(this.dropdownMenu.nativeElement).removeClass('show');
      if (!document.body.contains(this.data.input)) {
        // Element has been removed, probably because angular is re-rendering, try to get the element by its ID and focus the next one
        this.nextFocusable($(`#${this.data.input.id}`)).focus();
      }
    }
  }

  private nextFocusable(el: JQuery<HTMLElement>): JQuery<HTMLElement> {
    const focusable = $('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      .filter(':visible');
    const index = focusable.index(el[0]);
    return focusable.eq(index + 1);
  }

  private debouncedKeydown = debounce((event) => {
    if (!this.dropdownVisible()) {
      return;
    }
    const key = event.key;
    if (key === "ArrowDown" || key === "ArrowUp") {
      let newTabindex = key === "ArrowDown" ? this.activeIndex + 1 : this.activeIndex - 1;
      if (newTabindex < 0) {
        newTabindex = this.values.length - 1;
      }
      if (newTabindex >= this.values.length) {
        newTabindex = 0;
      }
      this.focusDropdownItem(newTabindex);
    }
    if (["Enter", "Tab"].includes(key)) {
      this.onSelect(this.activeIndex);
    }
  }, this.debounceWait);

  addListeners = () => {
    $(document).keydown((event) => this.debouncedKeydown(event));
  }

  focusFirstItem = () => this.focusDropdownItem(0)

  focusDropdownItem = (index: number) => {
    const newActiveElement = this.getElementAtIndex(index);
    const element = newActiveElement.get(0);
    if (element) {
      if (!this.isVisible(element, this.dropdownMenu.nativeElement)) {
        element.scrollIntoView({
          block: "nearest"
        });
      }
      setTimeout(() => this.activeChanged.emit(element), 0);
    }
    this.activeIndex = index;
    this.changeDetector.markForCheck();
  }

  isVisible = (element, container) => {
    const { bottom, height, top } = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const offset = 30;
    return top <= containerRect.top
      ? containerRect.top - (top - offset) <= height
      : bottom - (containerRect.bottom - offset) <= height;
  };

  private getElementAtIndex(index: number) {
    return $('[value=' + index + ']');
  }
}

interface TypeaheadData {
  input: HTMLElement;
  term: string;
  selectedValue: any;
  results: any[];
  resultFormatter: (value: any) => string;
  inputFormatter: (value: any) => string;
  resultTemplate: TemplateRef<any>;
  selectItem: EventEmitter<TypeaheadItem>;
  showDelete: boolean;
  deleteItem: EventEmitter<TypeaheadItem>
}

interface TypeaheadItem {
  item: any;
  value: string;
}
